import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';

const ACTION_COLORS = {
  page_view: 'bg-blue-100 text-blue-800',
  download: 'bg-red-100 text-red-800',
  export: 'bg-amber-100 text-amber-800',
  create: 'bg-emerald-100 text-emerald-800',
  update: 'bg-purple-100 text-purple-800',
  delete: 'bg-rose-100 text-rose-800',
  login: 'bg-indigo-100 text-indigo-800',
  share: 'bg-orange-100 text-orange-800',
  approve: 'bg-teal-100 text-teal-800',
};

const AuditLog = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('page', page);
      p.append('limit', '50');
      if (filterUser) p.append('user_id', filterUser);
      if (filterAction) p.append('action', filterAction);
      if (dateFrom) p.append('date_from', dateFrom);
      if (dateTo) p.append('date_to', dateTo);
      const res = await api.get(`/audit/logs?${p.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, filterUser, filterAction, dateFrom, dateTo]);

  const loadSummary = async () => {
    try {
      const res = await api.get('/audit/summary');
      setSummary(res.data);
    } catch {}
  };

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadSummary(); }, []);

  const totalPages = Math.ceil(total / 50);

  const formatTime = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2,'0');
      const mm = String(d.getMonth()+1).padStart(2,'0');
      const yy = String(d.getFullYear()).slice(-2);
      let h = d.getHours(); const min = String(d.getMinutes()).padStart(2,'0');
      const ampm = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
      return `${dd}-${mm}-${yy}, ${h}:${min} ${ampm}`;
    } catch { return ts; }
  };

  return (
    <div className="space-y-4 p-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="audit-log-title">Audit Log</h1>
        <p className="text-sm text-gray-500">Track all user activity — views, downloads, exports, and actions</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-summary">
          <Card className="border-l-4 border-l-blue-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Total Events</p>
            <p className="text-2xl font-bold text-blue-700">{(summary.total_logs || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-red-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Total Downloads</p>
            <p className="text-2xl font-bold text-red-700">{(summary.total_downloads || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Active Users (24h)</p>
            <p className="text-2xl font-bold text-emerald-700">{(summary.users_24h || []).length}</p>
          </CardContent></Card>
          <Card className="border-l-4 border-l-amber-500"><CardContent className="p-3">
            <p className="text-xs text-gray-500">Downloads (24h)</p>
            <p className="text-2xl font-bold text-amber-700">{(summary.users_24h || []).reduce((a, u) => a + (u.downloads || 0), 0)}</p>
          </CardContent></Card>
        </div>
      )}

      {/* 24h Activity per user */}
      {summary?.users_24h?.length > 0 && (
        <Card data-testid="audit-user-activity"><CardContent className="p-4">
          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">User Activity (Last 24 Hours)</p>
          <div className="flex flex-wrap gap-2">
            {summary.users_24h.map(u => (
              <div key={u.user_id} className="bg-gray-50 border rounded-lg px-3 py-2 text-xs min-w-[160px]">
                <p className="font-semibold text-gray-900">{u.user_name}</p>
                {u.watermark_code && <p className="text-gray-400 font-mono text-[10px]">Code: {u.watermark_code}</p>}
                <div className="flex gap-3 mt-1">
                  <span className="text-blue-600">Actions: <strong>{u.actions}</strong></span>
                  {u.downloads > 0 && <span className="text-red-600">Downloads: <strong>{u.downloads}</strong></span>}
                </div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Filters */}
      <Card data-testid="audit-filters"><CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs mb-1 block">From</Label>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="h-8 text-xs w-36" data-testid="audit-date-from" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">To</Label>
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="h-8 text-xs w-36" data-testid="audit-date-to" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Action</Label>
            <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} className="h-8 text-xs border rounded-md px-2 bg-white min-w-[120px]" data-testid="audit-action-filter">
              <option value="">All Actions</option>
              <option value="page_view">Page View</option>
              <option value="download">Download</option>
              <option value="export">Export</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="share">Share</option>
              <option value="approve">Approve</option>
            </select>
          </div>
          {(dateFrom || dateTo || filterAction || filterUser) && (
            <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={() => { setDateFrom(''); setDateTo(''); setFilterAction(''); setFilterUser(''); setPage(1); }}>Clear</Button>
          )}
          <span className="text-xs text-gray-400 ml-auto">{total} events</span>
        </div>
      </CardContent></Card>

      {/* Log Table */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-gray-400">Loading audit logs...</CardContent></Card>
      ) : (
        <Card data-testid="audit-log-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Time</th>
                    <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">User</th>
                    <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Code</th>
                    <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Action</th>
                    <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Page</th>
                    <th className="p-2.5 text-left font-semibold text-xs text-gray-500 uppercase bg-gray-50 border-b">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-400">No audit logs found.</td></tr>
                  ) : logs.map((log, i) => (
                    <tr key={i} className="hover:bg-gray-50 border-b border-gray-100" data-testid={`audit-row-${i}`}>
                      <td className="p-2.5 text-xs text-gray-500 whitespace-nowrap">{formatTime(log.timestamp)}</td>
                      <td className="p-2.5 font-semibold text-gray-900">{log.user_name}</td>
                      <td className="p-2.5 font-mono text-xs text-gray-400">{log.watermark_code || '—'}</td>
                      <td className="p-2.5">
                        <Badge className={`text-[10px] ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600'}`}>{log.action}</Badge>
                      </td>
                      <td className="p-2.5 text-gray-600">{log.page}</td>
                      <td className="p-2.5 text-xs text-gray-500 max-w-[300px] truncate">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t">
                <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AuditLog;
